import { useEffect, useState } from 'preact/hooks';
import { get } from '../../api';
import type { WeeklyObjective, WeeklyScore } from '../../types';
import { METRIC_DEFS, DEFAULT_TARGETS } from '../../types';
import { LcarsPanel } from '../layout/LcarsPanel';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface ObjectivesData {
  objective: WeeklyObjective;
  score: WeeklyScore;
}

function computeScore(actual: number, target: number, direction: string): number {
  if (direction === 'lower') {
    if (actual <= target * 0.6) return 3;
    if (actual <= target * 0.8) return 2;
    if (actual <= target) return 1;
    return 0;
  }
  const ratio = target > 0 ? actual / target : 0;
  if (ratio >= 1) return 3;
  if (ratio >= 0.8) return 2;
  if (ratio >= 0.6) return 1;
  return 0;
}

function fillPct(actual: number, target: number): number {
  if (target === 0) return 0;
  return Math.min(100, (actual / (target * 1.2)) * 100);
}

function fillClass(score: number): string {
  if (score >= 3) return 'bf-over';
  if (score >= 2) return 'bf-good';
  if (score >= 1) return 'bf-sat';
  return 'bf-poor';
}

function formatValue(val: number, format: string): string {
  if (format === 'pct') return Math.round(val) + '%';
  if (format === 'float') return val.toFixed(2);
  return String(Math.round(val));
}

const CATEGORY_GROUPS: { key: string; label: string; color: string }[] = [
  { key: 'volume', label: 'Pipeline throughput', color: 'var(--orange)' },
  { key: 'quality', label: 'Content quality', color: 'var(--blue)' },
  { key: 'efficiency', label: 'Operational efficiency', color: 'var(--lilac)' },
];

export function ObjectivesPanel() {
  const [data, setData] = useState<ObjectivesData | null>(null);

  useEffect(() => {
    get<ObjectivesData>('/api/objectives/current').then(setData).catch(console.error);
  }, []);

  if (!data) {
    return (
      <LcarsPanel color="var(--golden)" title="Objectives">
        <LoadingSpinner />
      </LcarsPanel>
    );
  }

  const { score } = data;
  const actuals: Record<string, number> = JSON.parse(score.actuals || '{}');
  const scores: Record<string, number> = JSON.parse(score.scores || '{}');
  const targets: Record<string, number> = (() => {
    try { return JSON.parse(data.objective.targets || '{}'); } catch { return DEFAULT_TARGETS; }
  })();

  const weekNum = (() => {
    try {
      const d = new Date(score.week_start);
      const start = new Date(d.getFullYear(), 0, 1);
      return Math.ceil(((d.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
    } catch { return '—'; }
  })();

  return (
    <LcarsPanel color="var(--golden)" title="Objectives" badge={`Wk ${weekNum}`}>
      {/* Score block */}
      <div class="score-block">
        <div class="score-wk-label">WEEKLY SCORE</div>
        <div class="score-row">
          <span class="score-num">{score.overall_score}</span>
          <span class="score-denom"> / {METRIC_DEFS.length * 3}</span>
          <span class="score-rank">{score.grade}</span>
        </div>
        <div class="score-bar-track">
          <div
            class="score-bar-fill"
            style={{ width: `${(score.overall_score / (METRIC_DEFS.length * 3)) * 100}%` }}
          ></div>
        </div>
        <div class="score-footer">
          <span class="score-rank-note">{score.grade} RANK</span>
        </div>
      </div>

      {/* Metric groups with bullet charts */}
      {CATEGORY_GROUPS.map((group) => {
        const metrics = METRIC_DEFS.filter((m) => m.category === group.key);
        return (
          <div key={group.key}>
            <div class="metric-group">
              <div class="metric-group-pip" style={{ background: group.color }}></div>
              <div class="metric-group-bar" style={{ background: group.color }}>
                <span class="metric-group-label">{group.label}</span>
              </div>
            </div>
            {metrics.map((metric) => {
              const actual = actuals[metric.key] ?? 0;
              const target = targets[metric.key] ?? DEFAULT_TARGETS[metric.key] ?? 1;
              const s = scores[metric.key] ?? computeScore(actual, target, metric.direction);
              const fp = fillPct(actual, target);

              return (
                <div class="bullet-row" key={metric.key}>
                  <div class="bullet-name" dangerouslySetInnerHTML={{ __html: metric.shortLabel.replace('\n', '<br>') }}></div>
                  <div class="bullet-chart">
                    <div class="bullet-zones">
                      <div class="bz-poor" style="width:50%"></div>
                      <div class="bz-sat" style="width:16.7%"></div>
                      <div class="bz-good" style="width:16.7%"></div>
                      <div class="bz-over" style="width:16.7%"></div>
                    </div>
                    <div class={`bullet-fill ${fp === 0 ? 'bf-zero' : fillClass(s)}`} style={{ width: `${fp}%` }}></div>
                    <div class="bullet-target" style="left:83.3%"></div>
                  </div>
                  <div class="bullet-vals">
                    <span class={`bv-actual s${s}`}>{formatValue(actual, metric.format)}</span>
                    <span class="bv-sep">/</span>
                    <span class="bv-target">{formatValue(target, metric.format)}</span>
                  </div>
                  <span class={`bullet-score bs-${s}`}>{s}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </LcarsPanel>
  );
}
